import { registerDestructor } from '@ember/destroyable';
import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import eq from 'editor/helpers/eq';
import gte from 'editor/helpers/gte';
import not from 'editor/helpers/not';
import pick from 'editor/helpers/pick';
import type Command from 'editor/models/command';
import { CompositeCommand } from 'editor/models/command';
import Drumset, { Instrument } from 'editor/models/drumset';
import { MidiEvent, PartType, SectionType } from 'editor/models/song';
import {
  AddMidiEventCommand,
  AddMidiEventsCommand,
  ChangeTrigPosCommand,
  DoubleBeatCommand,
  FilterOverlappingMidiEventsCommand,
  QuantizeTrackCommand,
  RemoveMidiEventCommand,
  RemoveMidiEventsCommand,
  UpdateMidiEventLengthCommand,
  UpdateMidiEventNoteCommand,
  UpdateMidiEventTickCommand,
  UpdateMidiEventVelocityCommand,
  UpdateTimeSignatureDenominatorCommand,
  UpdateTimeSignatureNumeratorCommand,
  UpdateTrackBarCountCommand,
} from 'editor/models/song-commands';
import disabled from 'editor/modifiers/disabled';
import onKey from 'editor/modifiers/on-key';
import selected from 'editor/modifiers/selected';
import PasteboardService from 'editor/services/pasteboard';
import SongPlayer, { SelectedTrack } from 'editor/services/song-player';
import UndoManagerService from 'editor/services/undo-manager';
import { bar, barBeat, beat, beatFactor, beatTicks } from 'editor/utils/music-utils';
import unique from 'editor/utils/unique';
import { modifier } from 'ember-modifier';
import { OverlayScrollbars } from 'overlayscrollbars';
import Button from './button';
import VelocityValues from './velocity-values';
import type ProjectManagerService from 'editor/services/project-manager';


function roundPixels(pixels: number) {
  return Math.round((pixels + Number.EPSILON) * 1000) / 1000;
}

function findHorizontalScroller(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const cs = getComputedStyle(node);
    const canXScroll =
      (cs.overflowX === 'auto' || cs.overflowX === 'scroll') &&
      node.scrollWidth > node.clientWidth;
    if (canXScroll) return node;
    node = node.parentElement;
  }
  return null;
}

// We need the modifier to re-run every time `ticks` changes
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
const updatePlayHead = modifier((element: HTMLElement, [ticks]: [number]) => {
  const scroller = findHorizontalScroller(element);
  if (!scroller) return;

  const elRect = element.getBoundingClientRect();
  const scRect = scroller.getBoundingClientRect();

  const elCenterX =
    (elRect.left - scRect.left) + scroller.scrollLeft + elRect.width / 2;

  const targetLeft = Math.max(
    0,
    Math.min(
      elCenterX - scroller.clientWidth / 2,
      scroller.scrollWidth - scroller.clientWidth
    )
  );

  // Only scroll left, never touch top. That way you can scroll up or down, unfettered
  scroller.scrollTo({ left: Math.round(targetLeft), behavior: 'auto' });
});

function intersectRect(r1: DOMRect, r2: DOMRect) {
  return !(
    r2.left > r1.right ||
    r2.right < r1.left ||
    r2.top > r1.bottom ||
    r2.bottom < r1.top
  );
}

type scrollDirection = 'x' | 'y' | 'both';
function getScrollParent(element: HTMLElement, xy: scrollDirection = 'both') {
  let parent: HTMLElement | null = element;
  const scrollHeight = xy === 'y' || xy === 'both';
  const scrollWidth = xy === 'x' || xy === 'both';

  while (parent) {
    if (
      (scrollHeight && parent.scrollHeight > parent.clientHeight) ||
      (scrollWidth && parent.scrollWidth > parent.clientWidth)
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return element;
}

function findElementAtPoint(
  x: number,
  y: number,
  match: string,
): HTMLElement | undefined {
  return <HTMLElement>document.elementsFromPoint(x, y).find((el) => el.matches(match));
}

function buildRect($element: HTMLElement, $offsetParent?: HTMLElement) {
  const rect = $element.getBoundingClientRect();
  const top = $element.offsetTop;
  const left = $element.offsetLeft;
  return {
    top: $offsetParent ? top + $offsetParent.offsetTop : top,
    left: $offsetParent ? left + $offsetParent.offsetLeft : left,
    width: rect.width,
    height: rect.height,
  };
}

function outerRect($elements: NodeList, $boundary: HTMLElement) {
  const outerRect = $boundary.getBoundingClientRect();
  let top = Number.MAX_VALUE,
    left = Number.MAX_VALUE,
    bottom = 0,
    right = 0;
  $elements.forEach(($el) => {
    const rect = (<HTMLElement>$el).getBoundingClientRect();
    const rtop = rect.top - outerRect.top + $boundary.offsetTop,
      rleft = rect.left - outerRect.left + $boundary.offsetLeft,
      rbottom = rect.bottom - outerRect.top,
      rright = rect.right - outerRect.left;
    if (rtop < top) {
      top = rtop;
    }
    if (rleft < left) {
      left = rleft;
    }
    if (rbottom > bottom) {
      bottom = rbottom;
    }
    if (rright > right) {
      right = rright;
    }
  });
  return { top, left, height: bottom - top, width: right - left };
}

const verticalOverlayScroll = modifier(
  ($element: HTMLElement, [isTrackMaximised]: [boolean]) => {
    const scrollTarget = <HTMLElement>document.querySelector('[data-scroll-target]');

    function repositionScrollTarget() {
      Object.assign(scrollTarget.style, {
        position: 'absolute',
        top: `${$element.scrollTop}px`,
        bottom: `${-$element.scrollTop}px`,
      });
    }
    $element.addEventListener('scroll', repositionScrollTarget);

    if (isTrackMaximised) {
      $element.scrollTop = 0;
      repositionScrollTarget();
    }

    return () => {
      $element.removeEventListener('scroll', repositionScrollTarget);
    };
  },
);

const horizontalOverlayScroll = modifier(($element: HTMLElement) => {
  const scrollTarget = <HTMLElement>document.querySelector('[data-scroll-target]');
  const scrollParent = getScrollParent($element);
  const parentElement = scrollParent.parentElement!;

  const resizeScrollTarget = function () {
    const scrollRect = scrollParent.getBoundingClientRect();
    const parentRect = parentElement.getBoundingClientRect()!;
    Object.assign(scrollTarget.style, {
      left: `${scrollRect.left - parentRect.left}px`,
    });
  };

  const resizeObserver = new ResizeObserver(resizeScrollTarget);
  resizeObserver.observe(parentElement);

  const instance = OverlayScrollbars(
    {
      target: $element,
      scrollbars: {
        slot: scrollTarget,
      },
    },
    {
      scrollbars: {
        theme: 'os-theme-dark',
        visibility: 'auto',
        autoHide: 'never',
        autoHideDelay: 1000,
        autoHideSuspend: true,
      },
    },
  );
  instance.on('updated', resizeScrollTarget);

  return () => {
    resizeObserver.disconnect();
    instance.destroy();
  };
});

const selectRegion = modifier(($element: HTMLElement, [match]: [string]) => {
  let selecting = false;
  let $selectRegion: HTMLDivElement | undefined = undefined;
  let elementRect: DOMRect | undefined = undefined;
  let regionRect: DOMRect | undefined = undefined;
  let $scrollYParent: HTMLElement;
  let $scrollXParent: HTMLElement;
  let startX = 0,
    startY = 0,
    top = 0,
    left = 0;
  let doubleClickTimer: number | undefined;

  const onMouseMove = (ev: MouseEvent) => {
    if (!selecting) {
      return;
    }
    if ($selectRegion) {
      window.getSelection()?.removeAllRanges();
      let width = ev.clientX + $scrollXParent.scrollLeft - startX,
        height = ev.clientY + $scrollYParent.scrollTop - startY;

      let t = top,
        l = left;

      if (width < 0) {
        l = left + width;
      }
      if (left + width > elementRect!.width) {
        width = elementRect!.width - left;
      }
      if (l < 0) {
        width -= l;
        l = 0;
      }

      if (height < 0) {
        t = top + height;
      }
      if (top + height > elementRect!.height) {
        height = elementRect!.height - top;
      }
      if (t < 0) {
        height -= t;
        t = 0;
      }

      regionRect!.x = l;
      regionRect!.y = t;
      regionRect!.width = width;
      regionRect!.height = height;

      Object.assign($selectRegion.style, {
        top: `${t}px`,
        left: `${l}px`,
        width: `${Math.abs(width)}px`,
        height: `${Math.abs(height)}px`,
        pointerEvents: 'none',
      });
      // find elements of matching class that intersect with the region
      const rect = $selectRegion.getBoundingClientRect();
      Array.from($element.querySelectorAll(match)).forEach((el) => {
        if (intersectRect(rect, el.getBoundingClientRect())) {
          el.dispatchEvent(
            new CustomEvent('bbff.select', {
              detail: {
                ctrlKey: true,
                metaKey: true,
                shiftKey: true,
                append: true,
              },
            }),
          );
        } else {
          if (!ev.shiftKey) {
            el.dispatchEvent(new CustomEvent('bbff.deselect'));
          }
        }
      });
    } else {
      if (Math.abs(startX - ev.clientX) > 5 || Math.abs(startY - ev.clientY) > 5) {
        $selectRegion = document.createElement('div');
        $selectRegion.classList.add('bbff-selection-box');
        Object.assign($selectRegion.style, {
          position: 'absolute',
          pointerEvents: 'none',
          top: `${top}px`,
          left: `${left}px`,
        });
        $element.appendChild($selectRegion);
      }
    }
  };

  const onMouseUp = (ev: MouseEvent) => {
    document.removeEventListener('mouseup', onMouseUp);
    document.removeEventListener('mousemove', onMouseMove);
    // If the mouse has not moved?
    if (
      Math.abs(startX - (ev.clientX + $scrollXParent.scrollLeft)) < 5 &&
      Math.abs(startY - (ev.clientY + $scrollYParent.scrollTop)) < 5
    ) {
      const $el = findElementAtPoint(ev.clientX, ev.clientY, match);
      if ($el) {
        $el.dispatchEvent(
          new CustomEvent('bbff.select', {
            detail: { ctrlKey: ev.ctrlKey, metaKey: ev.metaKey, shiftKey: ev.shiftKey },
          }),
        );
      }
      if (ev.detail >= 2) {
        clearTimeout(doubleClickTimer);
        document.elementFromPoint(ev.clientX, ev.clientY)?.dispatchEvent(
          new CustomEvent('bbff.add-event', {
            bubbles: true,
            detail: { x: regionRect!.x, y: regionRect!.y },
          }),
        );
      } else {
        doubleClickTimer = setTimeout(() => {
          if (!$el) {
            document.elementFromPoint(ev.clientX, ev.clientY)?.dispatchEvent(
              new CustomEvent('bbff.set-track-position', {
                bubbles: true,
                detail: { x: regionRect!.x },
              }),
            );
            if (!ev.shiftKey) {
              $element.dispatchEvent(new CustomEvent('bbff.deselect-all'));
            }
          }
        }, 300);
      }
    }
    if ($selectRegion) {
      $selectRegion.remove();
      $selectRegion = undefined;
    }
  };

  const onMouseDown = (ev: MouseEvent) => {
    selecting = false;
    elementRect = $element.getBoundingClientRect();
    $scrollYParent = getScrollParent($element, 'y');
    $scrollXParent = getScrollParent($element, 'x');

    const $el = findElementAtPoint(ev.clientX, ev.clientY, match);
    if (!$el) {
      selecting = true;
      $element.dispatchEvent(new CustomEvent('bbff.deselect-all'));
    }

    if (selecting) {
      window.getSelection()?.removeAllRanges();
    }

    startX = ev.clientX + $scrollXParent.scrollLeft;
    startY = ev.clientY + $scrollYParent.scrollTop;
    top = ev.clientY - elementRect!.top;
    left = ev.clientX - elementRect!.left;
    regionRect = new DOMRect();
    regionRect.x = left;
    regionRect.y = top;
    regionRect.width = 0;
    regionRect.height = 0;

    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mousemove', onMouseMove);
  };
  $element.addEventListener('mousedown', onMouseDown);

  return () => {
    $element.removeEventListener('mousedown', onMouseDown);
  };
});

const moveEvents = modifier(($boundaryElement: HTMLElement, [snapTo]: [number]) => {
  const eventMatch = '.midi-event',
    instrumentMatch = '.instrument';
  let $parent: HTMLElement;
  let $midiEventElement: HTMLElement;
  let $instrument: HTMLElement;
  let offsetX = 0,
    offsetY = 0,
    dragOffsetX = 0,
    dragOffsetY = 0,
    elementRect = { top: 0, left: 0, width: 0, height: 0 },
    selectedRect = { top: 0, left: 0, width: 0, height: 0 },
    boundaryRect = { top: 0, left: 0, width: 0, height: 0 };

  const moveTo = (x: number, y: number) => {
    offsetX = x;
    offsetY = y;
    $midiEventElement.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  };

  const onMouseMove = (ev: MouseEvent) => {
    ev.preventDefault();
    // has the mouse moved?
    let moved = true;
    if (
      Math.abs(ev.clientX - dragOffsetX) < 5 &&
      Math.abs(ev.clientY - dragOffsetY) < 5
    ) {
      moved = false;
      return;
    }

    let x = ev.clientX - dragOffsetX;
    let y = ev.clientY - dragOffsetY;
    const $instr = findElementAtPoint(ev.clientX, ev.clientY, instrumentMatch);
    if ($instr) {
      $instrument = $instr;
      if (elementRect.top + y != $instrument.offsetTop) {
        y = $instrument.offsetTop - elementRect.top;
      }
    }

    if (!ev.metaKey && !ev.ctrlKey) {
      x = Math.round(x / snapTo) * snapTo;
    }

    let selectionY = elementRect.top - selectedRect.top,
      selectionX = elementRect.left - selectedRect.left;

    // Make sure the combined selection doesn't move outside the boundary
    if (elementRect.top - selectionY + y < 0) {
      y = -selectionY;
    }
    if (selectedRect.top + selectedRect.height + y > boundaryRect.height) {
      y = boundaryRect.height - selectedRect.top - selectedRect.height;
    }
    if (elementRect.left - selectionX + x < boundaryRect.left) {
      x = boundaryRect.left + selectionX - elementRect.left;
    }
    if (
      selectedRect.left + selectedRect.width + x >
      boundaryRect.left + boundaryRect.width
    ) {
      x = boundaryRect.left + boundaryRect.width - selectedRect.left - selectedRect.width;
    }

    const instruments = y / elementRect.height;
    if (!ev.metaKey && !ev.ctrlKey) {
      x -= elementRect.left % snapTo;
    }

    $midiEventElement.dispatchEvent(
      new CustomEvent('bbff.move', {
        bubbles: true,
        detail: { moved, x, y, instruments, snap: !(ev.metaKey || ev.ctrlKey) },
      }),
    );

    moveTo(x, y);
  };

  const onMouseUp = (ev: MouseEvent) => {
    document.removeEventListener('mouseup', onMouseUp);
    document.removeEventListener('mousemove', onMouseMove);

    // has the mouse moved?
    let moved = true;
    if (
      Math.abs(ev.clientX - dragOffsetX) < 5 &&
      Math.abs(ev.clientY - dragOffsetY) < 5
    ) {
      moved = false;
      return;
    }

    const instruments = offsetY / elementRect.height;

    $midiEventElement.dispatchEvent(
      new CustomEvent('bbff.move-end', {
        bubbles: true,
        detail: { moved, x: offsetX, instruments, snap: !(ev.metaKey || ev.ctrlKey) },
      }),
    );

    offsetX = 0;
    offsetY = 0;
    dragOffsetX = 0;
    dragOffsetY = 0;
  };

  const onMouseDown = (ev: MouseEvent) => {
    boundaryRect = buildRect($boundaryElement);

    const $el = findElementAtPoint(ev.clientX, ev.clientY, eventMatch);
    if ($el && !ev.shiftKey) {
      if ($el && !$el.matches('.selected')) {
        $el.dispatchEvent(
          new CustomEvent('bbff.select', {
            bubbles: true,
            detail: { ctrlKey: ev.ctrlKey, metaKey: ev.metaKey, shiftKey: ev.metaKey },
          }),
        );
      }
      $instrument = <HTMLElement>(
        findElementAtPoint(ev.clientX, ev.clientY, instrumentMatch)
      );
      $midiEventElement = <HTMLElement>$el;
      $parent = <HTMLElement>$midiEventElement.parentElement;
      elementRect = buildRect($midiEventElement, $parent);

      if ($parent.offsetParent) {
        const selectedEvents = $boundaryElement.querySelectorAll(
          `${eventMatch}.selected`,
        );
        selectedRect = outerRect(selectedEvents, <HTMLElement>$parent.offsetParent);

        $midiEventElement.dispatchEvent(
          new CustomEvent('bbff.move-start', { bubbles: true, detail: {} }),
        );
      }
    } else {
      return;
    }

    dragOffsetX = ev.clientX - offsetX;
    dragOffsetY = ev.clientY - offsetY;

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const cancelDragNDrop = (e: DragEvent) => {
    e.preventDefault();
  };

  $boundaryElement.addEventListener('mousedown', onMouseDown);
  $boundaryElement.addEventListener('dragstart', cancelDragNDrop);

  return () => {
    $boundaryElement.removeEventListener('dragstart', cancelDragNDrop);
    $boundaryElement.removeEventListener('mousedown', onMouseDown);
  };
});

const changeVelocity = modifier(($boundaryElement: HTMLElement) => {
  const eventMatch = '.velocity-event';
  let $velocityElement: HTMLElement;
  let offsetY = 0,
    dragOffsetY = 0,
    elementRect = { top: 0, left: 0, width: 0, height: 0 },
    selectedRect = { top: 0, left: 0, width: 0, height: 0 },
    boundaryRect = { top: 0, left: 0, width: 0, height: 0 };

  const moveTo = (y: number) => {
    offsetY = y;
    $velocityElement.style.transform = `translate3d(0, ${y}px, 0)`;
  };

  const onMouseMove = (ev: MouseEvent) => {
    ev.preventDefault();
    let y = ev.clientY - dragOffsetY;

    const velocityChange = -Math.round((y / boundaryRect.height) * 127);

    // Make sure the combined selection doesn't move outside the boundary
    let selectionY = elementRect.top - selectedRect.top;
    if (elementRect.top - selectionY + y < boundaryRect.top) {
      y = boundaryRect.top - elementRect.top;
    }
    if (
      selectedRect.top + selectedRect.height + y >
      boundaryRect.top + boundaryRect.height
    ) {
      y = boundaryRect.top + boundaryRect.height - selectedRect.top - selectedRect.height;
    }

    $velocityElement.dispatchEvent(
      new CustomEvent('bbff.velocity-change', {
        bubbles: true,
        detail: {
          y,
          change: velocityChange,
          command: ev.metaKey || ev.ctrlKey,
        },
      }),
    );

    moveTo(y);
  };

  const onMouseUp = (/*ev: MouseEvent*/) => {
    document.removeEventListener('mouseup', onMouseUp);
    document.removeEventListener('mousemove', onMouseMove);

    $velocityElement.dispatchEvent(
      new CustomEvent('bbff.velocity-change-end', {
        bubbles: true,
        detail: {},
      }),
    );

    offsetY = 0;
    dragOffsetY = 0;
  };

  const onMouseDown = (ev: MouseEvent) => {
    const $el = findElementAtPoint(ev.clientX, ev.clientY, eventMatch);
    if ($el && !$el.matches('.selected')) {
      $el.dispatchEvent(
        new CustomEvent('bbff.select', {
          bubbles: true,
          detail: { ctrlKey: true, metaKey: true, shiftKey: true, append: true },
        }),
      );
    }
    if ($el && !ev.shiftKey) {
      $velocityElement = <HTMLElement>$el;
      elementRect = buildRect($velocityElement, $boundaryElement);

      const selectedEvents = $boundaryElement.querySelectorAll(`${eventMatch}.selected`);
      selectedRect = outerRect(selectedEvents, <HTMLElement>$boundaryElement);

      boundaryRect = buildRect($boundaryElement);
      boundaryRect.top = 0;

      $velocityElement.dispatchEvent(
        new CustomEvent('bbff.velocity-change-start', { bubbles: true, detail: {} }),
      );
    } else {
      return;
    }

    dragOffsetY = ev.clientY - offsetY;

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const cancelDragNDrop = (e: DragEvent) => {
    e.preventDefault();
  };

  $boundaryElement.addEventListener('mousedown', onMouseDown);
  $boundaryElement.addEventListener('dragstart', cancelDragNDrop);

  return () => {
    $boundaryElement.removeEventListener('dragstart', cancelDragNDrop);
    $boundaryElement.removeEventListener('mousedown', onMouseDown);
  };
});

const adjustLoopMarker = modifier(($loopMarker: HTMLElement, [snapTo]: [number]) => {
  let $parentElement: HTMLElement;
  let $leftHandle: HTMLElement;
  let offsetX = 0,
    startX = 0,
    dragOffsetX = 0,
    elementRect = { top: 0, left: 0, width: 0, height: 0 },
    parentRect = { top: 0, left: 0, width: 0, height: 0 };

  const moveTo = (x: number) => {
    x = Math.min(Math.max(0, x), parentRect.width - snapTo);
    const newWidth = Math.max(snapTo, elementRect.width + startX - x);
    offsetX = x;
    $loopMarker.style.marginLeft = `${x}px`;
    $loopMarker.style.width = `${newWidth}px`;
  };

  const onMouseMove = (ev: MouseEvent) => {
    ev.preventDefault();
    let x = ev.clientX - dragOffsetX;
    x = Math.round(x / snapTo) * snapTo;
    moveTo(x);
  };

  const onPointerUp = (ev: PointerEvent) => {
    document.removeEventListener('pointerup', onPointerUp);
    $leftHandle.releasePointerCapture(ev.pointerId);
  };

  const onMouseUp = (/*ev: MouseEvent*/) => {
    document.removeEventListener('mouseup', onMouseUp);
    document.removeEventListener('mousemove', onMouseMove);

    $leftHandle.dispatchEvent(
      new CustomEvent('bbff.trig-pos-change', {
        bubbles: true,
        detail: { x: offsetX },
      }),
    );

    offsetX = 0;
    dragOffsetX = 0;
  };

  const onPointerDown = (ev: PointerEvent) => {
    document.addEventListener('pointerup', onPointerUp);
    $leftHandle.setPointerCapture(ev.pointerId);
  };

  const onMouseDown = (ev: MouseEvent) => {
    if ($loopMarker.attributes.getNamedItem('aria-disabled')?.value === 'true') {
      return;
    }

    $parentElement = <HTMLElement>$loopMarker.parentElement;
    parentRect = buildRect($parentElement);
    elementRect = buildRect($loopMarker);

    offsetX = parseInt($loopMarker.style.marginLeft);
    startX = offsetX;
    dragOffsetX = ev.clientX - offsetX;

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const cancelDragNDrop = (e: DragEvent) => {
    e.preventDefault();
  };

  $leftHandle = $loopMarker.querySelector('.left-handle')!;
  if (!$leftHandle) {
    console.error('no left handle for loop marker');
    return;
  }
  $leftHandle.addEventListener('mousedown', onMouseDown);
  $leftHandle.addEventListener('pointerdown', onPointerDown);
  $leftHandle.addEventListener('dragstart', cancelDragNDrop);

  return () => {
    $leftHandle.removeEventListener('dragstart', cancelDragNDrop);
    $leftHandle.addEventListener('pointerdown', onPointerDown);
    $leftHandle.removeEventListener('mousedown', onMouseDown);
  };
});

const adjustMidiLength = modifier(($boundaryElement: HTMLElement, [snapTo]: [number]) => {
  const eventMatch = '.midi-event .rhandle';
  let $midiEventElement: HTMLElement;
  let $rightHandle: HTMLElement;
  let startX = 0,
    startWidth = 0,
    dragOffsetX = 0,
    elementRect = { top: 0, left: 0, width: 0, height: 0 };

  const moveTo = (x: number, width: number) => {
    $midiEventElement.style.left = `${x}px`;
    $midiEventElement.style.width = `${width}px`;
  };

  function calculateWidth(x: number, startX: number, startWidth: number, snap = false) {
    x = x + startX + startWidth;
    let minWidth = 1;
    if (snap) {
      x = Math.ceil(x / snapTo) * snapTo;
      minWidth = Math.floor((startX + snapTo) / snapTo) * snapTo - startX;
    }
    return Math.max(minWidth, x - startX);
  }

  const onMouseMove = (ev: MouseEvent) => {
    // disable moving
    ev.preventDefault();

    const x = ev.clientX - dragOffsetX;
    const width = calculateWidth(x, startX, startWidth, !(ev.metaKey || ev.ctrlKey));
    const adjustment = width - startWidth;

    $midiEventElement.dispatchEvent(
      new CustomEvent('bbff.resize', {
        bubbles: true,
        detail: { width, adjustment, snap: !(ev.metaKey || ev.ctrlKey) },
      }),
    );

    moveTo(startX, width);
  };

  const onPointerUp = (ev: PointerEvent) => {
    document.removeEventListener('pointerup', onPointerUp);
    $rightHandle?.releasePointerCapture(ev.pointerId);
  };

  const onMouseUp = (ev: MouseEvent) => {
    document.removeEventListener('mouseup', onMouseUp);
    document.removeEventListener('mousemove', onMouseMove);

    const x = ev.clientX - dragOffsetX;
    const width = calculateWidth(x, startX, startWidth, !(ev.metaKey || ev.ctrlKey));
    const adjustment = width - startWidth;

    $midiEventElement.dispatchEvent(
      new CustomEvent('bbff.resize-end', {
        bubbles: true,
        detail: { width, adjustment, snap: !(ev.metaKey || ev.ctrlKey) },
      }),
    );

    startX = 0;
    startWidth = 0;
    dragOffsetX = 0;
  };

  const onPointerDown = (ev: PointerEvent) => {
    document.addEventListener('pointerup', onPointerUp);
    const $el = findElementAtPoint(ev.clientX, ev.clientY, eventMatch);
    if ($el) {
      $el.setPointerCapture(ev.pointerId);
    }
  };

  const onMouseDown = (ev: MouseEvent) => {
    const $el = findElementAtPoint(ev.clientX, ev.clientY, eventMatch);
    if ($el) {
      ev.stopImmediatePropagation();
      $rightHandle = <HTMLElement>$el;
      $midiEventElement = <HTMLElement>$el.parentElement;
      if ($midiEventElement.attributes.getNamedItem('aria-disabled')?.value === 'true') {
        return;
      }
      $midiEventElement.dispatchEvent(
        new CustomEvent('bbff.resize-start', { bubbles: true, detail: {} }),
      );
    } else {
      return;
    }

    elementRect = buildRect($midiEventElement);

    startX = roundPixels(elementRect.left);
    startWidth = roundPixels(elementRect.width);
    dragOffsetX = ev.clientX;

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const cancelDragNDrop = (e: DragEvent) => {
    e.preventDefault();
  };

  $boundaryElement.addEventListener('mousedown', onMouseDown);
  $boundaryElement.addEventListener('pointerdown', onPointerDown);
  $boundaryElement.addEventListener('dragstart', cancelDragNDrop);

  return () => {
    $boundaryElement.removeEventListener('dragstart', cancelDragNDrop);
    $boundaryElement.addEventListener('pointerdown', onPointerDown);
    $boundaryElement.removeEventListener('mousedown', onMouseDown);
  };
});

function findParent($element: HTMLElement, match: string) {
  let $parent = $element.parentElement!;
  while ($parent && !$parent.matches(match)) {
    $parent = $parent.parentElement!;
  }
  return $parent;
}

const stickyTop = modifier(($element: HTMLElement, [parentMatch]: [string]) => {
  const $parent = findParent($element, parentMatch);

  const adjuster = () => {
    $element.style.top = `${$parent.scrollTop - 1}px`;
  };

  const resizeObserver = new ResizeObserver(() => {
    adjuster();
  });
  resizeObserver.observe($parent);

  const mutationObserver = new MutationObserver(() => {
    adjuster();
  });
  mutationObserver.observe($element, { attributes: true });

  $parent.addEventListener('scroll', adjuster);
  window.addEventListener('resize', adjuster);

  return () => {
    window.removeEventListener('resize', adjuster);
    $parent.removeEventListener('scroll', adjuster);
    mutationObserver.disconnect();
    resizeObserver.disconnect();
  };
});

const stickyBottom = modifier(($element: HTMLElement, [parentMatch]: [string]) => {
  const $parent = findParent($element, parentMatch);

  const bottomScrollBar = 1;

  const adjuster = () => {
    $element.style.bottom = `${Math.max(
      0,
      $parent.scrollHeight - $parent.scrollTop - $parent.clientHeight - bottomScrollBar,
    )}px`;
  };

  const resizeObserver = new ResizeObserver(() => {
    adjuster();
  });
  resizeObserver.observe($parent);

  const mutationObserver = new MutationObserver(() => {
    adjuster();
  });
  mutationObserver.observe($element, { attributes: true });

  adjuster();
  $parent.addEventListener('scroll', adjuster);
  window.addEventListener('resize', adjuster);

  return () => {
    window.removeEventListener('resize', adjuster);
    $parent.removeEventListener('scroll', adjuster);
    mutationObserver.disconnect();
    resizeObserver.unobserve($parent);
  };
});

interface TrackEditorSignature {
  Element: HTMLDivElement;
  Args: {
    drumset: Drumset;
    selectedTrack: SelectedTrack;
    trackPosition: number;
    isTrackMaximized: boolean;
    // eslint-disable-next-line no-unused-vars
    setTrackPosition: (position: number) => void;
  };
  Blocks: {
    // default: [];
  };
}

export default class TrackEditorComponent extends Component<TrackEditorSignature> {
  @service declare projectManager: ProjectManagerService;
  @service declare songPlayer: SongPlayer;
  @service declare undoManager: UndoManagerService;
  @service declare pasteboard: PasteboardService;
  @tracked _isCollapsed = true;
  private _previousDrumset: Drumset | null = null;
  private _previousSelectedTrack: SelectedTrack | null = null;
  @tracked division = 16;
  @tracked defaultVelocity = 100;
  @tracked theme: string | null = localStorage.getItem('theme');


  constructor(owner: unknown, args: TrackEditorSignature['Args']) {
    super(owner, args);
    this.isCollapsed = false;
    this.handleThemeChange = this.handleThemeChange.bind(this);
    window.addEventListener('themeChange', this.handleThemeChange);
    registerDestructor(this, () => {
      this.songPlayer.stop();
    });
  }

  handleThemeChange(event: Event) {
    this.theme = event.detail.theme;
  }

  get track() {
    return this.args.selectedTrack.track;
  }

  get trigPos() {
    return this.args.selectedTrack.trigPos;
  }

  get tickCount() {
    return this.args.selectedTrack.tickCount;
  }

  get trackPosition() {
    return this.args.trackPosition / this.step;
  }

  get isCollapsed() {
    return this.track.events.length > 0 && this._isCollapsed;
  }

  set isCollapsed(collapse: boolean) {
    this._isCollapsed = collapse;
  }

  get beatFactor() {
    return beatFactor(this.track.timeSignatureDenominator);
  }

  // Number of ticks per block at resolution (default: 16ths)
  get step() {
    return (
      (this.track.tickToQuarter * 4) / this.division
    );
  }

  get labelStep() {
    return (
      this.track.tickToQuarter / 2
    );
  }

  pixelsToTicks(pixels: number) {
    return (pixels / this.stepSize) * this.step;
  }

  ticksToPixels(ticks: number) {
    return (ticks / this.step) * this.stepSize;
  }

  get steps() {
    const length =
      (this.track.barCount * this.track.barLength) / this.step + this.extraSteps;
    return Array.from({ length }, (_, i) => i);
  }

  get numberingLabels() {
    // this is for the numbers (1, 1.1.3, 1.2, 1.2.3, etc)
    // per https://github.com/SingularSound/BBMO/issues/287, we want this to be constant
    this.division
    const length =
      (this.track.barCount * this.track.barLength) / this.labelStep;
    return Array.from({ length }, (_, i) => i);
  }

  // A fill track is allowed to have extra steps at the end of a fill
  //  and the fill is allowed to start on any step
  get isFillTrack() {
    return (
      this.args.selectedTrack.part.type != PartType.PART ||
      this.args.selectedTrack.sectionType !== SectionType.MAIN
    );
  }

  get extraSteps() {
    if (this.isFillTrack) {
      return 2;
    }
    return 0;
  }

  get stepSize() {
    const measureWidth = ((this.track.barCount * this.track.barLength) / 120 + this.extraSteps) * 40;
    return measureWidth / this.steps.length;
  }


  @action
  measureInstrumentHeight() {
    const firstInstrument = document.querySelector('.instrumentrow');
    if (firstInstrument) {
      return firstInstrument.getBoundingClientRect().height;
    }
  }

  @action
  beatLabel(step: number) {
    const bar = this.bar(step, true) + 1;
    const beat = this.barBeat(step, true);
    const subDivision = beat % 1;
    let label = `${bar}`;
    if (beat > 0) {
      label += `.${Math.floor(beat) + 1}`;
    }
    if (subDivision > 0) {
      label += `.3`;
    }
    return label;
  }

  @action
  bar(step: number, isForLabel = false) {
    return bar(
      step,
      isForLabel ? 8 : this.division,
      this.track.timeSignatureNumerator,
      this.track.timeSignatureDenominator,
    );
  }

  @action
  barBeat(step: number, isForLabel = false) {
    return barBeat(
      step,
      isForLabel ? 8 : this.division,
      this.track.timeSignatureNumerator,
      this.track.timeSignatureDenominator,
    );
  }

  @action
  beat(step: number) {
    return beat(
      step,
      this.division,
      this.track.timeSignatureNumerator,
      this.track.timeSignatureDenominator,
    );
  }

  @action
  stepColor(step: number) {
    if (Math.floor(this.beat(step)) % 2 == 0) {
      return ' bg-gray-100';
    } else {
      return '';
    }
  }

  // This border is not a solid vertical line because of bottom borders
  @action
  stepBorder(step: number) {
    const b = barBeat(
      step + 1,
      this.division,
      this.track.timeSignatureNumerator,
      this.track.timeSignatureDenominator,
    );
    if (b === 0) {
      return ' border-gray-600';
    } else {
      return '';
    }
  }

  @action
  trackStyle() {
    return htmlSafe(
      `width:${
        ((this.track.barCount * this.track.barLength) / this.step + this.extraSteps) *
        this.stepSize
      }px`,
    );
  }

  @action
  stepStyle() {
    const measureWidth = this.stepSize * (this.steps.length - this.extraSteps);
    return htmlSafe(`width:${measureWidth / (this.track.timeSignatureNumerator * (8 / this.track.timeSignatureDenominator) * this.track.barCount)}px`);
  }

  //this god forsaken function is the cause of the table not regenerating properly when the styles change
  @action
  divisionStyle(step: number) {
    let leftColor, rightColor;
    if (this.theme === 'dark') {
      // per https://github.com/SingularSound/BBMO/issues/287,
      // the coloring for 1/4T should have 1 to 1.2 shaded one color, and 1.2 to 1.3 shaded another
      // to achieve this, in the below case, we color the second and 5th beat by halves
      if ((this.division == 6) && (((step % 6) === 1) || ((step % 6) === 4))) {
        rightColor = '#171717';
        leftColor = '#1C1C1C';
      } else {
        if (Math.floor(this.beat(step)) % 2 === 0){
          leftColor = '#1C1C1C';
          rightColor = '#1C1C1C';
        } else {
          leftColor = '#171717';
          rightColor = '#171717';
        }
      }
    } else {
      if ((this.division == 6) && (((step % 6) === 1) || ((step % 6) === 4))) {
        rightColor = '#FFFFFF';
        leftColor = '#F3F4F6';
      } else {
        if (Math.floor(this.beat(step)) % 2 === 0){
          leftColor = '#F3F4F6';
          rightColor = '#F3F4F6';
        } else {
          leftColor = '#FFFFFF';
          rightColor = '#FFFFFF';
        }
      }
    }

  const borderColor = this.theme === 'dark' ? '#292B2E' : '#E5E7EB';

  return htmlSafe(
    `position:absolute;top:0;bottom:0;border-right:1px solid ${borderColor};background: linear-gradient(to right, ${leftColor} 0%, ${leftColor} 50%, ${rightColor} 50%, ${rightColor} 100%);width:${this.stepSize}px;left:${step * this.stepSize}px`
  );
}
  get lineStyle() {
    return htmlSafe(`height:${this.stepSize}px`);
  }

  get playHeadStyle() {
    if (this.songPlayer.ticks > 0) {
      let maxPos = this.ticksToPixels(this.track.totalTicks);
      const position = this.ticksToPixels(this.songPlayer.ticks);
      const left = Math.min(maxPos, position);
      return htmlSafe(`left:${left}px;width:1px`);
    } else {
      return htmlSafe(`display:none;`);
    }
  }

  get trackPositionStyle() {
    if (this.selectedEvents.length) {
      return htmlSafe(`display:none;`);
    } else {
      // This will reset the track position if the track changes.
      let maxPos = this.ticksToPixels(this.track.totalTicks);
      if (this.isFillTrack) {
        maxPos += 2 * this.stepSize;
      }
      const left = Math.min(maxPos, Math.floor(this.trackPosition * this.stepSize)) - 1;
      return htmlSafe(`left:${left}px;width:1px`);
    }
  }

  get loopMarkerStyle() {
    const left = this.ticksToPixels(Math.floor(this.trigPos));
    const width = this.ticksToPixels(Math.floor(this.tickCount));
    return htmlSafe(`margin-left:${left}px;width:${width}px`);
  }

  @action
  eventStyle(event: MidiEvent) {
    const left = this.ticksToPixels(this.trigPos + event.tick);
    const width = this.ticksToPixels(event.length);
    const saturation = event.velocity / 127 + 0.5;
    return htmlSafe(`left:${left}px;width:${width}px;filter:saturate(${saturation})`);
  }

  @action
  velocityStyle(event: MidiEvent) {
    const left = this.ticksToPixels(this.trigPos + event.tick);
    const width = event.length / 3 - 7; // to this day, nobody knows why we divide by three
    const bottom = (event.velocity / 127) * 100;
    return htmlSafe(`left:${left}px;width:${width}px;bottom:${bottom}%;`);
  }

  @action
  eventsForInstrument(instrument: Instrument) {
    return this.track.eventsForInstrument(instrument.id);
  }

  @action
  toggleCollapsed() {
    if (this.track.events.length) {
      this.isCollapsed = !this.isCollapsed;
      this.scrollToBottom();
    }
  }

  @tracked selectedEvents: MidiEvent[] = [];

  get selectedEventsInCurrentTrack() {
    return this.selectedEvents.filter((ev) => this.track.events.includes(ev));
  }

  @action
  toggleSelectEvent(event: MidiEvent, evt?: Event) {
    let ev = <CustomEvent>evt;
    if (ev?.detail?.shiftKey | ev.detail?.ctrlKey | ev.detail?.metaKey) {
      if (this.isEventSelected(event)) {
        if (!ev?.detail?.append) {
          this.selectedEvents = this.selectedEvents.filter((e) => e !== event);
        }
      } else {
        this.selectedEvents = [...this.selectedEvents, event];
      }
    } else {
      this.selectedEvents = [event];
    }
  }

  @action
  clearSelectedEvents() {
    this.selectedEvents = [];
    this.undoManager.appendCommand(
      new FilterOverlappingMidiEventsCommand(this.track),
      false,
    );
  }

  @action
  removeSelectedEvent(event: MidiEvent) {
    if (!this.isEventSelected(event)) {
      return;
    }
    this.selectedEvents = this.selectedEvents.filter((e) => e !== event);
  }

  @action
  isEventSelected(event: MidiEvent) {
    return this.selectedEvents.includes(event);
  }

  @action
  async deleteSelectedMidiEvents() {
    const eventsToDelete = this.selectedEventsInCurrentTrack;
    if (!eventsToDelete.length) {
      return;
    }
    const commands = eventsToDelete.map(
      (event) => new RemoveMidiEventCommand(this.track, event),
    );
    await this.undoManager.executeCommand(
      new CompositeCommand('Remove MIDI events', commands),
    );
    this.selectedEvents = this.selectedEvents.filter(
      (ev) => !eventsToDelete.includes(ev),
    );
  }

  @action
  selectEvents(instrument: Instrument, ev: MouseEvent) {
    const trackEvents = this.track.eventsForInstrument(instrument.id);
    if (ev.shiftKey) {
      if (this.selectedEvents.some((event) => trackEvents.includes(event))) {
        this.selectedEvents = this.selectedEvents.filter(
          (event) => !trackEvents.includes(event),
        );
      } else {
        this.selectedEvents = [...this.selectedEvents, ...trackEvents];
      }
      return;
    }
    this.selectedEvents = this.track.eventsForInstrument(instrument.id);
  }

  @action
  selectAll(ev: KeyboardEvent) {
    ev.preventDefault();
    this.selectedEvents = this.track.events;
  }
 
  @action
  removeMissingNotesFromSelected() {
    this.selectedEvents = this.selectedEvents.filter((ev) =>
      this.track.events.includes(ev),
    );
  }

  @action
  copyMidi() {
    if (!this.selectedEvents.length) {
      return;
    }
    this.removeMissingNotesFromSelected();
    if (!this.selectedEvents.length) {
      return;
    }
    this.pasteboard.store('midi-events', this.selectedEvents);
    const minTick = Math.min(...this.selectedEvents.map((event) => event.tick));
    this.args.setTrackPosition(Math.round(minTick / this.step));
  }

  @action
  async cutMidi() {
    this.copyMidi();
    const eventsToRemove = this.selectedEventsInCurrentTrack;
    if (!eventsToRemove.length) {
      return;
    }
    const cmd = new RemoveMidiEventsCommand(this.track, eventsToRemove);
    cmd.name = 'Cut MIDI events';
    await this.undoManager.executeCommand(cmd);
    this.selectedEvents = this.selectedEvents.filter(
      (ev) => !eventsToRemove.includes(ev),
    );
  }

  @action
  async pasteMidi() {
    const events = (<MidiEvent[]>this.pasteboard.retrieve('midi-events') ?? []).map(
      (event) => event.copy(),
    );
    //set the shortest event tick to zero at the track select position and adjust all the rest by a relative amount
    const minTick = Math.min(...events.map((event) => event.tick));
    events.forEach((event) => {
      event.tick = event.tick - minTick + this.trackPosition * this.step;
    });
    const cmd = new AddMidiEventsCommand(this.track, events);
    cmd.name = 'Paste MIDI events';
    await this.undoManager.executeCommand(cmd);
    this.selectedEvents = events;
  }

  @action
  setTrackPosition(evt: Event) {
    const ev = <CustomEvent>evt;
    if (this.selectedEvents.length > 0) {
      return;
    }
    const trackPosition = Math.round(ev.detail.x / this.stepSize);
    const positionInTicks = Math.max(this.trigPos, trackPosition * this.step);
    this.args.setTrackPosition?.(positionInTicks);
  }

  @action
  changeTrigPos(evt: Event) {
    const ev = <CustomEvent>evt;
    const trigPosStep = Math.round(ev.detail.x / this.stepSize);
    // If the track position is now behind the trigger position, update the track position
    if (trigPosStep > this.trackPosition) {
      this.setTrackPosition(evt);
    }
    const trigPos = this.pixelsToTicks(ev.detail.x);
    this.undoManager.executeCommand(new ChangeTrigPosCommand(this.track, trigPos));
  }

  @tracked highlightedInstrument?: Instrument;
  @action
  highlightInstrument(instrument: Instrument) {
    this.highlightedInstrument = instrument;
  }

  @action
  async addMidiEvent(instrument: Instrument, evt: Event) {
    const ev = <CustomEvent>evt;
    const step = Math.floor(ev.detail.x / this.stepSize);
    const tick = step * this.step;
    if (tick < this.trigPos) {
      return;
    }
    const event = await this.undoManager.executeCommand(
      new AddMidiEventCommand(
        this.track,
        tick - this.trigPos,
        instrument.id,
        this.defaultVelocity,
        this.step,
      ),
    );
    this.selectedEvents = [event];
  }

  @action
  playInstrument(instrument: Instrument) {
    this.songPlayer.audioContext.resume();
    this.songPlayer.playInstrument(instrument, 127);
  }

  @action
  playFrom(step: number) {
    this.songPlayer.playFrom(step * this.labelStep);
  }

  // All playable and required instruments
  get allInstruments() {
    return this.args.drumset.playableInstruments
      .concat(this.requiredInstruments)
      .filter(unique)
      .sort((a, b) => b.id - a.id);
  }

  get visibleInstruments() {
    if (this._previousDrumset !== this.args.drumset || this._previousSelectedTrack !== this.args.selectedTrack) {
      // this is to fix previous issue fixed by PR #259 while also fixing issue #653
      // before, it scrolled to bottom whenever visible instruments length changed.
      // however, this caused issues when moving all notes up/down when folded
      // since that could cause the number of visible instruments to change (if all notes moved out of view of a folded track, it disappears)
      // but to cover other test cases fixed by PR #259, we check if the drumset or selected track/song changed
      this._previousDrumset = this.args.drumset;
      this._previousSelectedTrack = this.args.selectedTrack;
      this.scrollToBottom();
    }
    return this.allInstruments.filter(
      (instrument) => !this.isCollapsed || this.track.instruments.includes(instrument.id),
    );
  }

  // Instruments required by the selected track (may not be playable by the selected drumset)
  get requiredInstruments() {
    return this.track.instruments.map((id) => this.args.drumset.instruments[id]!);
  }

  eventResizes: { event: MidiEvent; start: number; width: number; length: number }[] = [];
  @action
  handleResizeStart(event: MidiEvent) {
    if (!this.isEventSelected(event)) {
      this.selectedEvents = [event];
    }
    this.eventResizes = this.selectedEventsInCurrentTrack.map((event) => ({
      event,
      start: this.ticksToPixels(event.tick),
      length: event.length,
      width: this.ticksToPixels(event.length),
    }));
  }

  doResize(evt: Event, saveToUndo = false) {
    const ev = <CustomEvent>evt;
    const adjustment = ev.detail.adjustment;
    const undoCommands = this.eventResizes.map(({ event, start, width, length }) => {
      let newWidth = width + adjustment;
      if (ev.detail.snap) {
        let end = start + newWidth;
        end = Math.floor(end / this.stepSize) * this.stepSize;
        newWidth = end - start;
        if (newWidth < this.stepSize) {
          newWidth = this.stepSize;
        }
      }
      const newLength = this.pixelsToTicks(newWidth);
      event.length = newLength;
      return new UpdateMidiEventLengthCommand(this.track, event, newLength, length);
    });
    if (saveToUndo) {
      this.undoManager.executeCommand(
        new CompositeCommand('Update MIDI note length', undoCommands),
      );
    }
  }

  @action
  handleResize(evt: Event) {
    this.doResize(evt);
  }

  @action
  handleResizeEnd(evt: Event) {
    this.doResize(evt, true);
    this.selectedEvents = this.eventResizes.map(({ event }) => event);
    this.eventResizes = [];
  }

  eventMoves: { event: MidiEvent; note: number; tick: number }[] = [];
  moveInstruments?: Instrument[];
  @action
  handleMoveStart() {
    this.moveInstruments = [...this.visibleInstruments];
    this.eventMoves = this.selectedEventsInCurrentTrack.map((event) => ({
      event,
      note: event.note,
      tick: event.tick,
    }));
  }

  doMove(evt: Event, saveToUndo = false) {
    const ev = <CustomEvent>evt;
    if (ev.detail.moved) {
      const x = ev.detail.x;
      const undoCommands: Command<any>[] = [];
      let deltaTicks = this.pixelsToTicks(x);
      if (ev.detail.snap) {
        deltaTicks = Math.round(deltaTicks / this.step) * this.step;
      }
      this.eventMoves.forEach(({ event, tick, note }) => {
        let newTick = tick + deltaTicks;
        event.tick = newTick;
        undoCommands.push(
          new UpdateMidiEventTickCommand(this.track, event, newTick, tick),
        );

        if (saveToUndo) {
          let newNote: number | undefined = note;
          if (ev.detail.instruments) {
            const instrumentIndex = this.moveInstruments!.findIndex(
              (instrument) => instrument.id === event.note,
            );
            newNote = this.moveInstruments?.[instrumentIndex + ev.detail.instruments]?.id;
            if (newNote) {
              event.note = newNote;
              undoCommands.push(new UpdateMidiEventNoteCommand(event, newNote, note));
            }
          }
        }
      });
      if (saveToUndo) {
        this.undoManager.executeCommand(
          new CompositeCommand('Move MIDI events', undoCommands),
        );
      } else {
        // eslint-disable-next-line ember/no-runloop, ember/no-incorrect-calls-with-inline-anonymous-functions
        scheduleOnce('afterRender', this, function () {
          document.querySelectorAll('.midi-event.selected').forEach(($el) => {
            (<HTMLElement>$el).style.transform = `translate3d(0, ${ev.detail.y}px, 0)`;
          });
        });
      }
    }
  }

  @action
  handleMove(evt: Event) {
    this.doMove(evt);
  }

  @action
  handleMoveEnd(evt: Event) {
    this.doMove(evt, true);

    const $el = evt.target as HTMLElement;
    $el.style.transform = 'none';
    this.moveInstruments = undefined;
    this.selectedEvents = this.eventMoves.map(({ event }) => event);
    this.eventMoves = [];
  }

  @action
  moveEventsUp(ev: KeyboardEvent) {
    ev.preventDefault();
    const events = this.selectedEventsInCurrentTrack;
    if (!events.length) {
      return;
    }
    const moveBy = ev.altKey ? 12 : 1;
    const minNote = Math.min(...events.map((event) => event.note));
    const minNoteIndex = this.allInstruments.findIndex(
      (instrument) => instrument.id === minNote,
    );
    if (minNoteIndex - moveBy < 0) {
      return;
    }
    const commands = events.map((event) => {
      const instrumentIndex = this.allInstruments.findIndex(
        (instrument) => instrument.id === event.note,
      );
      const instrument = this.allInstruments[instrumentIndex - moveBy]!;
      return new UpdateMidiEventNoteCommand(event, instrument.id);
    });
    this.undoManager.executeCommand(
      new CompositeCommand('Change MIDI instrument', commands),
    );
  }

  @action
  moveEventsDown(ev: KeyboardEvent) {
    ev.preventDefault();
    const events = this.selectedEventsInCurrentTrack;
    if (!events.length) {
      return;
    }
    const moveBy = ev.altKey ? 12 : 1;
    const maxNote = Math.max(...events.map((event) => event.note));
    const maxNoteIndex = this.allInstruments.findIndex(
      (instrument) => instrument.id === maxNote,
    );
    if (maxNoteIndex + moveBy > this.allInstruments.length - 1) {
      return;
    }
    const commands = events.map((event) => {
      const instrumentIndex = this.allInstruments.findIndex(
        (instrument) => instrument.id === event.note,
      );
      const instrument = this.allInstruments[instrumentIndex + moveBy]!;
      return new UpdateMidiEventNoteCommand(event, instrument.id);
    });
    this.undoManager.executeCommand(
      new CompositeCommand('Change MIDI instrument', commands),
    );
  }

  @action
  moveEventsLeft(ev: KeyboardEvent) {
    ev.preventDefault();
    const events = this.selectedEventsInCurrentTrack;
    if (!events.length) {
      return;
    }
    const minTick = Math.min(...events.map((event) => event.tick));
    if (minTick < this.step) {
      return;
    }
    const commands = events.map(
      (event) =>
        new UpdateMidiEventTickCommand(this.track, event, event.tick - this.step),
    );
    this.undoManager.executeCommand(
      new CompositeCommand('Move MIDI events left', commands),
    );
  }

  @action
  moveEventsRight(ev: KeyboardEvent) {
    ev.preventDefault();
    const events = this.selectedEventsInCurrentTrack;
    if (!events.length) {
      return;
    }
    const rightMostTick =
      Math.max(...events.map((event) => event.tick)) +
      this.step; /*tick width*/
    let maxTick = this.track.tickCount;
    if (this.isFillTrack) {
      maxTick = this.track.tickCount + 2 * this.step;
    }
    if (rightMostTick + this.step > maxTick) {
      return;
    }
    const commands = events.map(
      (event) =>
        new UpdateMidiEventTickCommand(this.track, event, event.tick + this.step),
    );
    this.undoManager.executeCommand(
      new CompositeCommand('Move MIDI events right', commands),
    );
  }

  activeVelocityEvent?: { event: MidiEvent; velocity: number };
  @action
  toggleSelectVelocity(event: MidiEvent) {
    this.activeVelocityEvent = { event, velocity: event.velocity };
    if (!this.isEventSelected(event)) {
      this.selectedEvents = [event];
    }
  }

  eventVelocities: any[] = [];
  @action
  handleVelocityChangeStart() {
    this.eventVelocities = this.selectedEventsInCurrentTrack.map((event) => ({
      event,
      velocity: event.velocity,
    }));
  }

  @action
  handleVelocityChange(ev: Event) {
    const evt = <CustomEvent>ev;
    const velocityChange = evt.detail.change;

    if (this.activeVelocityEvent) {
      const adjustedVelocity = this.activeVelocityEvent.velocity + velocityChange;

      const percentageChange =
        (adjustedVelocity - this.activeVelocityEvent.velocity) /
        (this.activeVelocityEvent.velocity == 0 ? 1 : this.activeVelocityEvent.velocity);

      this.eventVelocities.forEach((change) => {
        const event = <MidiEvent>change.event;
        // The active velocity event (the one being dragged) is always changed to the exact value
        if (event === this.activeVelocityEvent?.event) {
          event.velocity = Math.round(change.velocity + velocityChange);
        } else {
          if (evt.detail.command) {
            event.velocity = Math.round(change.velocity + velocityChange);
          } else {
            event.velocity = Math.round((change.velocity == 0 ? 1 : change.velocity) * (1 + percentageChange));
          }
        }
      });
    }
  }

  @action
  handleVelocityChangeEnd(ev: Event) {
    const evt = <CustomEvent>ev;
    const undoCommands: Command<any>[] = [];
    this.eventVelocities.forEach((change) => {
      const event = <MidiEvent>change.event;
      undoCommands.push(
        new UpdateMidiEventVelocityCommand(event, change.velocity, event.velocity),
      );
    });
    this.undoManager.executeCommand(
      new CompositeCommand('Change Velocities', undoCommands),
    );

    const $el = evt.target as HTMLElement;
    $el.style.transform = 'none';
    this.selectedEvents = this.eventVelocities.map((change) => change.event);
    this.eventVelocities = [];
    this.activeVelocityEvent = undefined;
  }

  barCountShiftKey = false;
  barCountCtrlKey = false;
  @action
  barCountKeydown(ev: MouseEvent) {
    this.barCountShiftKey = ev.shiftKey;
    this.barCountCtrlKey = ev.ctrlKey || ev.metaKey;
  }

  @action
  changeBarCount(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const barCount = parseInt(input.value);
    if (isNaN(barCount)) { input.value = String(this.track.barCount); return; }
    this.undoManager.executeCommand(
      new UpdateTrackBarCountCommand(
        this.track,
        barCount,
        this.barCountShiftKey /* inFront */,
        this.barCountCtrlKey /* copyEvents */,
      ),
    );
    this.barCountShiftKey = false;
    this.barCountCtrlKey = false;
  }

  @action
  changeTimeSignatureNumerator(numerator: string) {
    this.undoManager.executeCommand(
      new UpdateTimeSignatureNumeratorCommand(this.track, parseInt(numerator), this.args.selectedTrack.sectionType),
    );
  }

  @action
  changeTimeSignatureDenominator(denominatorValue: string) {
    const denominator = parseInt(denominatorValue);
    this.undoManager.executeCommand(
      new UpdateTimeSignatureDenominatorCommand(this.track, denominator),
    );
    if (this.division < denominator) {
      this.division = denominator;
    }
  }

  @action
  scrollToBottom() {
    const scrollParent = document.querySelector('.scroll-parent');
    if (scrollParent) {
      scheduleOnce('afterRender', this, function () {
        scrollParent.scrollTop = this.measureInstrumentHeight() * (this.visibleInstruments.length - 1);
      });
    }
  }

  @action
  doubleBeat() {
    this.undoManager.executeCommand(new DoubleBeatCommand(this.track));
  }

  @action
  quantizeTrack() {
    this.undoManager.executeCommand(new QuantizeTrackCommand(this.track, this.division));
  }

  @action
  changeDivision(ev: Event) {
    this.division = parseInt((<HTMLInputElement>ev.target).value);
  }

  divisionName(division: number) {
    switch (division) {
      case 2:
        return 'Half';
      case 4:
        return 'Quarter';
      case 6:
        return '1/4T';
      case 8:
        return 'Eighth';
      case 12:
        return '1/8T';
      case 16:
        return '16th';
      case 24:
        return '1/16T';
      case 32:
        return '32nd';
      case 64:
        return '64th';
    }
  }

  @action
  setDefaultVelocity(velocity: number) {
    this.defaultVelocity = velocity;
  }

  <template>
    {{! template-lint-disable no-invalid-interactive }}
    <div class="flex flex-col w-full h-full overflow-auto relative" ...attributes>
      <div
        data-test-hides-track-and-instrument-overflow
        class="absolute w-full bottom-0 z-10 h-2 bg-white dark:bg-primary-darkComponent"
      ></div>
      <nav aria-label="beat navigation" class="flex flex-row items-center gap-2 shrink-0">
        <label>Bar count:
          <input
            type="number"
            min="1"
            step="1"
            inputmode="numeric"
            autocomplete="off"
            onwheel="this.blur()"
            oninput="this.value = this.value.replace(/\D+/g, '')"
            value={{this.track.barCount}}
            class="w-16 px-2 py-1 pr-1 text-right border rounded dark:bg-primary-darkComponent dark:border-primary-darkBorderColor"
            {{! template-lint-disable no-pointer-down-event-binding }}
            {{on "mousedown" this.barCountKeydown}}
            {{on "change" this.changeBarCount}}
          /></label>
        {{! template-lint-disable no-nested-interactive }}
        <label class="flex items-center gap-1">Time signature:
          <div class="flex flex-col px-2 py-1 text-xs border rounded dark:border-primary-darkBorderColor">
            <select
              class="border-b dark:border-primary-darkBorderColor dark:bg-primary-darkComponent"
              {{on "change" (pick "target.value" this.changeTimeSignatureNumerator)}}
            >
              {{#each (array 2 3 4 6 8 9 12) as |numerator|}}
                <option
                  value={{numerator}}
                  selected={{eq this.track.timeSignatureNumerator numerator}}
                >{{numerator}}</option>
              {{/each}}
              <option disabled>─</option>
              {{! prettier-ignore }}
              {{#each (array 1 5 7 10 11 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30 31 32) as |numerator|}}
                <option
                  value={{numerator}}
                  selected={{eq this.track.timeSignatureNumerator numerator}}
                >{{numerator}}</option>
              {{/each}}
            </select>
            <select
              class="dark:bg-primary-darkComponent"
              {{on "change" (pick "target.value" this.changeTimeSignatureDenominator)}}
            >
              {{#each (array 2 4 8 16 32) as |numerator|}}
                <option
                  value={{numerator}}
                  selected={{eq this.track.timeSignatureDenominator numerator}}
                >{{numerator}}</option>
              {{/each}}
            </select>
          </div>
        </label>
        {{#if (not (eq @selectedTrack.sectionType SectionType.FILL))}}
        <Button {{on "click" this.doubleBeat}}>Double Beat</Button>
        {{/if}}
        <Button
          @icon="bullseye"
          title="Quantize track"
          {{on "click" this.quantizeTrack}}
        />


        <label class="flex items-center gap-1 ml-auto">Division:
          <select
            class="flex flex-col px-2 py-1 text-xs border rounded dark:border-primary-darkBorderColor dark:bg-primary-darkComponent"
            {{on "change" this.changeDivision}}
          >
            {{#each (array 2 4 6 8 12 16 24 32 64) as |division|}}
              {{#if (gte division this.track.timeSignatureDenominator)}}
                <option
                  value={{division}}
                  selected={{eq division this.division}}
                >{{this.divisionName division}}</option>
              {{/if}}
            {{/each}}
          </select>
        </label>
      </nav>
      <div
        class="relative flex w-full overflow-auto border-t border-l scroll-parent pb-2 dark:border-primary-darkBorderColor"
        {{on "bbff.set-track-position" this.setTrackPosition}}
        {{on "bbff.trig-pos-change" this.changeTrigPos}}
        {{verticalOverlayScroll @isTrackMaximized}}
      >
        <div data-scroll-target class="absolute inset-0 z-50 pointer-events-none"></div>
        <div data-test-instruments class="flex flex-col w-1/5 max-w-max h-max">
          <div
            class="sticky top-0 left-0 z-20 w-full bg-white border-b border-gray-400 top-bar dark:bg-primary-darkComponent dark:border-primary-darkBorderColor"
          >
            <nav class="flex flex-col justify-end h-8">
              <button
                type="button"
                class="text-[0.6rem] bg-gray-200 selected:bg-primary selected:text-white py-0.5 px-1 h-4 dark:bg-primary-darkBorderColor"
                {{selected when=this.isCollapsed}}
                {{on "click" this.toggleCollapsed}}
              >{{if this.isCollapsed "Unfold" "Fold"}}</button>
            </nav>
          </div>
          {{#each this.visibleInstruments as |instrument|}}
            <div class="instrumentrow flex flex-row w-full h-5 text-sm border-b border-r shrink-0 dark:border-primary-darkBorderColor">
              <button
                type="button"
                class="px-1 overflow-hidden text-left grow cursor-vol whitespace-nowrap text-ellipsis selected:bg-primary/15"
                title={{instrument.name}}
                {{selected when=(eq this.highlightedInstrument instrument)}}
                {{on "mouseover" (fn this.highlightInstrument instrument)}}
                {{on "click" (fn this.playInstrument instrument)}}
              >{{instrument.id}}. {{instrument.name}}</button>
              <button
                type="button"
                class="w-6 bg-gray-300 shrink-0 dark:bg-primary-darkHeader"
                {{on "mouseover" (fn this.highlightInstrument instrument)}}
                {{on "click" (fn this.selectEvents instrument)}}
              >&nbsp;</button>
            </div>
          {{/each}}
          <div
            class="sticky bottom-0 left-0 z-20 flex flex-col justify-between w-full h-16 px-1 text-sm text-left bg-white border-b border-r shrink-0 dark:border-primary-darkBorderColor dark:bg-primary-darkComponent"
          >
            <h2>Velocity</h2>
            <VelocityValues
              @events={{this.selectedEvents}}
              @defaultValue={{this.defaultVelocity}}
              @setDefault={{this.setDefaultVelocity}}
            />
          </div>
        </div>
        <div class="relative w-full overflow-x-scroll h-max" {{horizontalOverlayScroll}}>
          <div
            class="absolute top-0 left-0 z-20 bg-white border-b border-gray-400 w-max top-bar dark:border-primary-darkBorderColor dark:bg-primary-darkComponent"
            {{stickyTop ".scroll-parent"}}
          >
            <div
              data-test-bar-marker
              class="flex items-start h-4 text-[0.6rem] w-max z-10 bg-white dark:bg-primary-darkFill"
            >
              {{#each this.numberingLabels as |step|}}
                <div
                  class="h-4 pl-1 border-r shrink-0 grow cursor-vol dark:bg-primary-darkFill dark:border-primary-darkBorderColor
                    {{this.stepColor step}}
                    {{this.stepBorder step}}"
                  style={{(this.stepStyle)}}
                  {{on "click" (fn this.playFrom step)}}
                >{{this.beatLabel step}}</div>
              {{/each}}
            </div>
            <div
              data-test-loop-marker
              class="relative w-max h-4 text-[0.6rem] bg-primary-light disabled:bg-gray-200 dark:bg-primary-darkFill dark:disabled:bg-primary-darkFill"
              {{disabled when=(eq @selectedTrack.sectionType SectionType.MAIN)}}
            >
              <div
                class="h-4 bg-primary disabled:bg-gray-300 loop-marker"
                style={{this.loopMarkerStyle}}
                {{disabled when=(not this.isFillTrack)}}
                {{adjustLoopMarker this.stepSize}}
              >
                <div class="absolute w-4 h-4 left-handle cursor-ew-resize"></div>
              </div>
            </div>
          </div>
          <div
            data-test-tracks
            class="relative flex flex-col items-start w-full mt-8 mb-16"
            style={{(this.trackStyle)}}
            {{selectRegion ".midi-event"}}
            {{adjustMidiLength this.stepSize}}
            {{moveEvents this.stepSize}}
            {{onKey "Backspace" this.deleteSelectedMidiEvents}}
            {{onKey "Delete" this.deleteSelectedMidiEvents}}
            {{onKey "a" this.selectAll cmdKey=true}}
            {{onKey "c" this.copyMidi cmdKey=true}}
            {{onKey "v" this.pasteMidi cmdKey=true}}
            {{onKey "x" this.cutMidi cmdKey=true}}
            {{onKey "ArrowUp" this.moveEventsUp}}
            {{onKey "ArrowUp" this.moveEventsUp altKey=true}}
            {{onKey "ArrowDown" this.moveEventsDown}}
            {{onKey "ArrowDown" this.moveEventsDown altKey=true}}
            {{onKey "ArrowLeft" this.moveEventsLeft}}
            {{onKey "ArrowRight" this.moveEventsRight}}
            {{on "bbff.deselect-all" this.clearSelectedEvents}}
            {{on "bbff.resize" this.handleResize}}
            {{on "bbff.resize-end" this.handleResizeEnd}}
            {{on "bbff.move-start" this.handleMoveStart}}
            {{on "bbff.move" this.handleMove}}
            {{on "bbff.move-end" this.handleMoveEnd}}
          >
            {{! Draws the vertical step divisions }}
            {{#each this.steps as |step|}}
              <div class="step" style={{this.divisionStyle step}}></div>
            {{/each}}
            {{! Draws the horizontal instrument dividing lines }}
            {{#each this.visibleInstruments as |instrument|}}
              <div
                class="relative w-full h-5 border-t dark:border-primary-darkBorderColor instrument selected:bg-primary/15"
                {{selected when=(eq this.highlightedInstrument instrument)}}
                {{on "mouseover" (fn this.highlightInstrument instrument)}}
                {{on "bbff.add-event" (fn this.addMidiEvent instrument)}}
              ></div>
            {{/each}}
            {{! Draws the individual notes }}
            <div class="absolute top-0 bottom-0 left-0 right-0 pointer-events-none">
              {{#each this.visibleInstruments as |instrument|}}
                <div class="relative flex h-5 instrument">
                  {{#each (this.eventsForInstrument instrument) as |event|}}
                    <div
                      class="absolute top-0 left-0 h-5 bg-orange-400 pointer-events-auto midi-event"
                      style={{this.eventStyle event}}
                      {{selected when=(this.isEventSelected event)}}
                      {{on "mouseover" (fn this.highlightInstrument instrument)}}
                      {{on "bbff.select" (fn this.toggleSelectEvent event)}}
                      {{on "bbff.deselect" (fn this.removeSelectedEvent event)}}
                      {{on "bbff.resize-start" (fn this.handleResizeStart event)}}
                    ><div class="rhandle"></div></div>
                  {{/each}}
                </div>
              {{/each}}
            </div>
          </div>
          <div
            data-test-velocity
            class="absolute bottom-0 left-0 z-20 flex flex-col items-start bg-white border-t w-max border-gray-400 dark:bg-primary-darkFill dark:border-primary-darkBorderColor"
            style={{(this.trackStyle)}}
            {{stickyBottom ".scroll-parent"}}
            {{on "bbff.deselect-all" this.clearSelectedEvents}}
          >
            <div class="relative flex h-16 border-b dark:border-primary-darkBorderColor">
              {{! Draws vertical step divisions }}
              {{#each this.steps as |step|}}
                <div class="step" style={{this.divisionStyle step}}></div>
              {{/each}}
              {{! Draws the individual velocity markers }}
              <div
                class="absolute bottom-0 left-0 right-0 top-2"
                {{selectRegion ".velocity-event"}}
                {{changeVelocity}}
                {{on "bbff.velocity-change-start" this.handleVelocityChangeStart}}
                {{on "bbff.velocity-change" this.handleVelocityChange}}
                {{on "bbff.velocity-change-end" this.handleVelocityChangeEnd}}
                {{on "bbff.deselect-all" this.clearSelectedEvents}}
              >
                {{#each this.track.noteOnEvents as |event|}}
                  <div
                    class="absolute left-0 h-px velocity-event"
                    style={{this.velocityStyle event}}
                    data-test-velocity={{event.velocity}}
                    {{selected when=(this.isEventSelected event)}}
                    {{! template-lint-disable no-pointer-down-event-binding }}
                    {{on "pointerdown" (fn this.toggleSelectVelocity event)}}
                  ></div>
                {{/each}}
              </div>
            </div>
          </div>
          <div
            data-test-play-head
            {{updatePlayHead this.songPlayer.ticks}}
            class="absolute top-0 bottom-0 left-0 mt-8 bg-red-500"
            style={{this.playHeadStyle}}
            {{stickyTop ".scroll-parent"}}
            {{stickyBottom ".scroll-parent"}}
          ></div>
          <div
            data-test-track-position
            class="absolute bottom-0 left-0 mt-8 bg-primary -top-px track-pos"
            style={{this.trackPositionStyle}}
            {{stickyTop ".scroll-parent"}}
            {{stickyBottom ".scroll-parent"}}
          ></div>
        </div>
      </div>
    </div>
  </template>
}
